import { useState, useRef, useCallback, useEffect } from 'react';
import Sidebar from '../../../components/sidebar/Sidebar';
import './AnnouncementsPage.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:9090/api';

function getToken() { return localStorage.getItem('token'); }

const SERVERS = [
  { key: 'ultimate',   name: 'UltimateTV' },
  { key: 'experience', name: 'ExperienceTV' },
  { key: 'extreme',    name: 'ExtremeTV' },
  { key: 'nectar',     name: 'NectarTV' },
  { key: 'vulture',    name: 'VultureTV' },
];

const CHANNEL_TYPES = [
  { value: 'announcements', label: 'Announcements' },
  { value: 'updates',       label: 'Updates' },
  { value: 'maintenance',   label: 'Maintenance' },
];

const PING_TYPES = [
  { value: 'none',        label: 'No ping' },
  { value: 'everyone',    label: '@everyone' },
  { value: 'here',        label: '@here' },
  { value: 'service_one',   label: 'Service ONE' },
  { value: 'service_two',   label: 'Service TWO' },
  { value: 'service_three', label: 'Service THREE' },
  { value: 'service_four',  label: 'Service FOUR' },
  { value: 'beta_testing',  label: 'Beta Testers' },
];

// Grouped so the UI can render them with section labels
const PLACEHOLDER_GROUPS = [
  {
    label: 'Broadcast',
    items: ['[ANNOUNCEMENTS_CHANNEL]', '[UPDATES_CHANNEL]', '[MAINTENANCE_CHANNEL]'],
  },
  {
    label: 'Community',
    items: ['[SUGGESTIONS_CHANNEL]', '[HELP_CENTER_CHANNEL]', '[SERVICE_POLICY_CHANNEL]', '[REFERRAL_PROGRAM_CHANNEL]', '[PUBLIC_REFERRAL_CHANNEL]'],
  },
  {
    label: 'Operations',
    items: ['[PAYMENTS_CHANNEL]', '[OVERDUE_PAYMENTS_CHANNEL]', '[SHOP_CHANNEL]', '[LIVE_ISSUES_CHANNEL]', '[SERVER_REQUESTS_CHANNEL]', '[SERVER_REPORTS_CHANNEL]'],
  },
  {
    label: 'Other',
    items: ['[SETUP_CHANNEL]', '[GENERATE_TRIAL_CHANNEL]', '[FIND_VOD_CHANNEL]', '[INFORMATION_CHANNEL]', '[PERKS_CHANNEL]'],
  },
  {
    label: 'Beta & AI',
    items: ['[AI_HELPDESK_CHANNEL]', '[BETA_NEWS_CHANNEL]', '[BETA_CHAT_CHANNEL]'],
  },
];

const TOOLBAR_ITEMS = [
  { label: 'B',   wrap: ['**', '**'],   title: 'Bold' },
  { label: 'I',   wrap: ['*', '*'],     title: 'Italic' },
  { label: 'S',   wrap: ['~~', '~~'],   title: 'Strikethrough' },
  { label: '`',   wrap: ['`', '`'],     title: 'Inline code' },
  { label: '```', wrap: ['```\n', '\n```'], title: 'Code block', newline: true },
  { label: '#',   wrap: ['# ', ''],     title: 'Heading', newline: true },
  { label: '>',   wrap: ['> ', ''],     title: 'Quote', newline: true },
];

/* ── Minimal Discord markdown → HTML renderer ──────────────────────────────── */
function renderDiscordPreview(text) {
  if (!text) return '';
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  return escaped
    .replace(/```[\w]*\n?([\s\S]+?)```/g, '<pre><code>$1</code></pre>')
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/~~(.+?)~~/g, '<s>$1</s>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')
    .replace(/\n/g, '<br/>');
}

/* ── ServerCheckboxList ─────────────────────────────────────────────────────── */
function ServerCheckboxList({ title, selected, onChange }) {
  const allSelected = SERVERS.every(s => selected.includes(s.key));

  const toggle = (key) => {
    onChange(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const toggleAll = () => {
    onChange(allSelected ? [] : SERVERS.map(s => s.key));
  };

  return (
    <div className="an-field">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <span className="an-label">{title}</span>
        <button className="an-toggle-all" onClick={toggleAll} type="button">
          {allSelected ? 'Deselect all' : 'Select all'}
        </button>
      </div>
      <div className="an-server-list">
        {SERVERS.map(s => (
          <label key={s.key} className="an-server-row">
            <input
              type="checkbox"
              checked={selected.includes(s.key)}
              onChange={() => toggle(s.key)}
            />
            <span className="an-server-name">{s.name}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/* ── Main page ──────────────────────────────────────────────────────────────── */
export default function AnnouncementsPage() {
  const [markdown, setMarkdown]           = useState('');
  const [channelType, setChannelType]     = useState('announcements');
  const [pingType, setPingType]           = useState('none');
  const [discordServers, setDiscordServers] = useState(SERVERS.map(s => s.key));
  const [telegramGroups, setTelegramGroups] = useState(SERVERS.map(s => s.key));
  const [files, setFiles]                 = useState([]);
  const [sending, setSending]             = useState(false);
  const [results, setResults]             = useState(null);
  const [dragOver, setDragOver]           = useState(false);
  const [fileError, setFileError]         = useState(null);

  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  /* ── Toolbar wrap ── */
  const insertWrap = useCallback(({ wrap, newline }) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end   = ta.selectionEnd;
    const sel   = markdown.slice(start, end);
    const [pre, post] = wrap;
    const prefix = newline && start > 0 && markdown[start - 1] !== '\n' ? '\n' : '';
    const inserted = prefix + pre + sel + post;
    const next = markdown.slice(0, start) + inserted + markdown.slice(end);
    setMarkdown(next);
    setTimeout(() => {
      const cursor = start + prefix.length + pre.length + sel.length;
      ta.setSelectionRange(cursor, cursor);
      ta.focus();
    }, 0);
  }, [markdown]);

  /* ── Placeholder insert ── */
  const insertPlaceholder = useCallback((ph) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const pos = ta.selectionStart;
    const next = markdown.slice(0, pos) + ph + markdown.slice(pos);
    setMarkdown(next);
    setTimeout(() => {
      ta.setSelectionRange(pos + ph.length, pos + ph.length);
      ta.focus();
    }, 0);
  }, [markdown]);

  /* ── File handling ──
     Discord rejects a webhook attachment over 10 MB on an unboosted guild, so
     an oversized file is refused here with a readable message rather than
     failing at the webhook with a status nobody sees. */
  const MAX_FILE_BYTES = 10 * 1024 * 1024;
  const MAX_FILES = 10;

  const addFiles = useCallback((newFiles) => {
    const incoming = Array.from(newFiles);
    const tooBig = incoming.filter(f => f.size > MAX_FILE_BYTES);
    const ok = incoming.filter(f => f.size <= MAX_FILE_BYTES);

    if (tooBig.length) {
      setFileError(
        `${tooBig.map(f => f.name).join(', ')} ${tooBig.length === 1 ? 'is' : 'are'} over 10 MB — `
        + 'Discord will not accept that in a webhook. Compress it and try again.'
      );
    } else {
      setFileError(null);
    }

    setFiles(prev => {
      const room = MAX_FILES - prev.length;
      if (room <= 0) {
        setFileError(`You can attach at most ${MAX_FILES} files to one announcement.`);
        return prev;
      }
      if (ok.length > room) {
        setFileError(`Only the first ${room} of those fit — ${MAX_FILES} files is the limit.`);
      }
      return [...prev, ...ok.slice(0, room).map(f => ({
        file: f,
        url: URL.createObjectURL(f),
        isVideo: f.type.startsWith('video/'),
      }))];
    });
  }, []);

  const removeFile = (idx) => {
    setFiles(prev => {
      URL.revokeObjectURL(prev[idx].url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  useEffect(() => {
    return () => files.forEach(f => URL.revokeObjectURL(f.url));
  }, []); // eslint-disable-line

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  /* ── Send ── */
  const send = async () => {
    if (!markdown.trim()) return;
    if (discordServers.length === 0 && telegramGroups.length === 0) return;

    setSending(true);
    setResults(null);
    try {
      const form = new FormData();
      form.append('markdown', markdown);
      form.append('channelType', channelType);
      form.append('pingType', pingType);
      form.append('discordServers', JSON.stringify(discordServers));
      form.append('telegramGroups', JSON.stringify(telegramGroups));
      files.forEach(f => form.append('files', f.file));

      const res = await fetch(`${API}/announcements/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: form,
      });
      const data = await res.json();
      setResults(data.results || []);
    } catch (err) {
      setResults([{ ok: false, error: err.message, target: 'request' }]);
    } finally {
      setSending(false);
    }
  };

  const charCount = markdown.length;
  const canSend = markdown.trim().length > 0 && (discordServers.length > 0 || telegramGroups.length > 0) && !sending;

  return (
    <>
      <Sidebar title="Server Management" />
      <div className="an-page">
        <div className="an-header">
          <i className="bi bi-megaphone-fill" />
          Send Announcement
        </div>

        <div className="an-layout">
          {/* ── Left: Compose ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="an-card">
              <div className="an-card-title">
                <i className="bi bi-pencil-square" />
                Message
              </div>

              <div className="an-toolbar">
                {TOOLBAR_ITEMS.map((t, i) => (
                  <button key={i} className="an-tool-btn" title={t.title} onClick={() => insertWrap(t)} type="button">
                    {t.label}
                  </button>
                ))}
                <div className="an-tool-divider" />
                <button className="an-tool-btn" title="@everyone" onClick={() => insertPlaceholder('@everyone')} type="button">@all</button>
                <button className="an-tool-btn" title="@here" onClick={() => insertPlaceholder('@here')} type="button">@here</button>
              </div>

              <textarea
                ref={textareaRef}
                className="an-textarea"
                placeholder="Write your announcement in Discord markdown..."
                value={markdown}
                onChange={e => setMarkdown(e.target.value)}
                spellCheck={false}
              />

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                <span style={{ fontSize: '0.68rem', color: 'var(--color-subtle)' }}>{charCount} chars</span>
              </div>

              <div className="an-placeholder-groups">
                {PLACEHOLDER_GROUPS.map(group => (
                  <div key={group.label} className="an-placeholder-group">
                    <span className="an-placeholder-group-label">{group.label}</span>
                    <div className="an-placeholder-row">
                      {group.items.map(ph => (
                        <button key={ph} className="an-ph-btn" onClick={() => insertPlaceholder(ph)} type="button">
                          {ph.replace(/^\[|\]$/g, '')}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Media upload ── */}
            <div className="an-card">
              <div className="an-card-title">
                <i className="bi bi-image" />
                Media (optional)
              </div>

              <div
                className={`an-upload-zone${dragOver ? ' drag-over' : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
              >
                <i className="bi bi-cloud-upload" />
                Drop images or videos here, or click to browse
                <br />
                <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>Up to 10 files · 10 MB each</span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*"
                style={{ display: 'none' }}
                onChange={e => { if (e.target.files.length) addFiles(e.target.files); e.target.value = ''; }}
              />

              {fileError && (
                <div style={{
                  marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: '0.78rem',
                  background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)',
                  color: '#f87171',
                }}>
                  <i className="bi bi-exclamation-triangle-fill me-2" />{fileError}
                </div>
              )}

              {files.length > 0 && (
                <div className="an-media-grid">
                  {files.map((f, i) => (
                    <div key={i} className="an-media-thumb">
                      {f.isVideo
                        ? <video src={f.url} muted />
                        : <img src={f.url} alt={f.file.name} />
                      }
                      <button className="an-media-remove" onClick={() => removeFile(i)} title="Remove">×</button>
                      <div className="an-media-label">{f.file.name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Right: Options + Preview ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="an-card">
              <div className="an-card-title">
                <i className="bi bi-sliders" />
                Options
              </div>

              <div className="an-field">
                <label className="an-label">Channel Type</label>
                <select className="an-select" value={channelType} onChange={e => setChannelType(e.target.value)}>
                  {CHANNEL_TYPES.map(ct => (
                    <option key={ct.value} value={ct.value}>{ct.label}</option>
                  ))}
                </select>
              </div>

              <div className="an-field">
                <label className="an-label">Ping Role</label>
                <select className="an-select" value={pingType} onChange={e => setPingType(e.target.value)}>
                  {PING_TYPES.map(pt => (
                    <option key={pt.value} value={pt.value}>{pt.label}</option>
                  ))}
                </select>
              </div>

              <ServerCheckboxList
                title="Discord Servers"
                selected={discordServers}
                onChange={setDiscordServers}
              />

              <ServerCheckboxList
                title="Telegram Groups"
                selected={telegramGroups}
                onChange={setTelegramGroups}
              />

              <button
                className="an-send-btn"
                onClick={send}
                disabled={!canSend}
                type="button"
              >
                {sending
                  ? <><i className="bi bi-arrow-repeat" style={{ animation: 'spin 1s linear infinite' }} /> Sending…</>
                  : <><i className="bi bi-send-fill" /> Send Announcement</>
                }
              </button>

              {results && results.length > 0 && (
                <div className="an-results">
                  {results.map((r, i) => (
                    <div key={i} className={`an-result-row ${r.ok ? 'an-result-ok' : 'an-result-fail'}`}>
                      <i className={`bi ${r.ok ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`} />
                      <span>{r.name || r.target}{r.error ? ` — ${r.error}` : ''}</span>
                      <span className="an-result-platform">{r.platform}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Preview ── */}
            <div className="an-card">
              <div className="an-card-title">
                <i className="bi bi-discord" />
                Preview
              </div>
              {markdown
                ? <div
                    className="an-preview"
                    dangerouslySetInnerHTML={{ __html: renderDiscordPreview(markdown) }}
                  />
                : <div className="an-preview" style={{ color: '#72767d', fontStyle: 'italic' }}>
                    Your message will appear here…
                  </div>
              }
            </div>
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
